import {
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import {
  CustomerUser,
  customerUsersApi,
  SearchExternalUser,
} from '@texas/api/endpoints/customerUsersApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { BodyOverride } from '@texas/components/shared/BodyOverride';
import { useSignalSubscriptionEvents } from '@texas/components/shared/hooks/useSignalSubscriptionEvents';
import { TexasHeader } from '@texas/components/shared/TexasHeader';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { customerUserEvents } from './events';
import { useValueDisclosure } from '@texas/hooks/useValueDisclosure';
import { SetCustomTypeModal } from './SetCustomTypeModal';
import { SearchFilterInput } from '@texas/components/SearchFilterInput';
import { externalUsersColumns } from './ExternalUsersColumns';
import { DataTableContainer } from '@texas/components/shared/dataTable/DataTableContainer';
import { CreateExternalBrandUser } from '@texas/components/users/CreateExternalBrandUser';
import { userEvents } from '@texas/components/users/events';
import { TexasSwitch } from '@texas/components/shared/form/TexasSwitch';
import { ArchiveUser } from '@texas/components/users/ArchiveUser';
import { Role, userApi } from '@texas/api/endpoints/userApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { ServerError } from '@texas/types';
import { request } from '@texas/utils/helpers/httpHelpers';
import { FeatureGuard } from '@texas/components/shared/featureGuard/FeatureGuard';
import { VerifyDialogWithRequest } from '@texas/components/shared/dialog/VerifyDialogWithRequest';
import { RoleRestrict } from '@texas/components/shared/RoleRestrict';

interface Props {
  customerId: number;
  customerName: string;
}

export function ExternalUsers({ customerId, customerName }: Props) {
  const { t } = useTranslation();
  const toast = useToast();

  const [searchQuery, setSearchQuery] = useState<SearchExternalUser>({
    customerId: customerId,
    searchTerm: '',
    includeArchived: false,
    sortBy: 'userName',
    sortDesc: false,
  });

  const {
    data: users,
    refetch,
    loading,
    error,
  } = useApiResource(customerUsersApi.getExternalUsers);

  const {
    isOpen: isCreateUserOpen,
    onClose: onCreateUserClose,
    onOpen: onCreateUserOpen,
  } = useDisclosure();

  const {
    isOpen: isOpenCustomType,
    onClose: onCloseCustomType,
    value: valueCustomType,
    onOpen: onOpenCustomType,
  } = useValueDisclosure<{
    id: number;
    customType: string;
  }>();

  const {
    isOpen: isOpenArchive,
    onClose: onCloseArchive,
    value: valueArchive,
    onOpen: onArchiveOpen,
  } = useValueDisclosure<{ id: number; email: string }>();

  const {
    isOpen: isOpenSetMain,
    onClose: onCloseSetMain,
    value: valueSetMain,
    onOpen: onSetMainOpen,
  } = useValueDisclosure<{ id: number; name: string }>();

  useEffect(() => {
    refetch(searchQuery);
  }, [searchQuery, refetch]);

  useSignalSubscriptionEvents({
    triggerOnEvent: () => {
      refetch(searchQuery);
    },
    signalEvents: [
      customerUserEvents.customerUserAdded,
      customerUserEvents.customerUserRemoved,
      customerUserEvents.onCustomerUserSetMain,
      customerUserEvents.onCustomerUserCustomTypeChanged,
      userEvents.newUserCreated,
    ],
  });
  const { request: restoreUserRequest } = useApiRequest(userApi.restore);

  const performRestore = useCallback(
    async (user: CustomerUser) => {
      await request(
        restoreUserRequest,
        [user.userId],
        () => {
          toast({
            title: t('general.entityRestored', {
              name: user.userName || user.email,
            }),
            status: 'success',
            isClosable: true,
          });
          refetch(searchQuery);
        },
        (error: ServerError) => {
          toast({
            title: t('general.restoreFailed'),
            description: error.message,
            status: 'error',
            isClosable: true,
          });
        },
      );
    },
    [restoreUserRequest, toast, t, refetch, searchQuery],
  );

  const columns = useMemo(() => {
    return externalUsersColumns(
      t,
      onOpenCustomType,
      onArchiveOpen,
      performRestore,
      onSetMainOpen,
    );
  }, [t, onOpenCustomType, onArchiveOpen, performRestore, onSetMainOpen]);

  return (
    <FeatureGuard>
      <BodyOverride>
        <Card variant="dark">
          <CardBody>
            <Flex direction="column" gap={4}>
              <TexasHeader text={t('customerUser.externalUsers')} />
              <Flex justify="space-between">
                <Box height="fit-content" mt="auto" w={400}>
                  <SearchFilterInput
                    value={searchQuery.searchTerm}
                    placeholder={t('contacts.searcNameOrEmail')}
                    onChange={(s) =>
                      setSearchQuery({ ...searchQuery, searchTerm: s })
                    }
                  />
                </Box>
                <RoleRestrict
                  allow={[Role.SystemAdministrator, Role.InternalAdministrator]}
                >
                  <Button variant="texas-solid" onClick={onCreateUserOpen}>
                    {t('user.createUser')}
                  </Button>
                </RoleRestrict>
              </Flex>
              <TexasSwitch
                checked={searchQuery.includeArchived}
                label={t('general.includeArchived')}
                onChange={(checked) =>
                  setSearchQuery({
                    ...searchQuery,
                    includeArchived: checked,
                  })
                }
              />
              <DataTableContainer
                w="100%"
                error={error}
                datatable={{
                  data: users ?? [],
                  isLoading: loading,
                  columns: columns,
                  variant: 'configuration',
                  sorted: {
                    onSortedChange: ({ key, desc }) => {
                      setSearchQuery({
                        ...searchQuery,
                        sortBy: key,
                        sortDesc: desc,
                      });
                    },
                    key: searchQuery.sortBy,
                    desc: searchQuery.sortDesc,
                  },
                }}
              />
            </Flex>
          </CardBody>
        </Card>
        <CreateExternalBrandUser
          customerId={customerId}
          customerName={customerName}
          onClose={onCreateUserClose}
          isOpen={isCreateUserOpen}
        />
        {valueArchive && (
          <ArchiveUser
            user={{ id: valueArchive.id, email: valueArchive.email }}
            isOpen={isOpenArchive}
            onClose={onCloseArchive}
            onArchive={() => customerUserEvents.customerUserRemoved.dispatch()}
          />
        )}
        {valueCustomType && (
          <SetCustomTypeModal
            customerUserId={valueCustomType.id}
            customType={valueCustomType.customType}
            onUpdate={() => {
              customerUserEvents.onCustomerUserCustomTypeChanged.dispatch();
              onCloseCustomType();
            }}
            isOpen={isOpenCustomType}
            onClose={onCloseCustomType}
          />
        )}
        {valueSetMain && (
          <VerifyDialogWithRequest
            headerTitle={t('customerUser.setMain')}
            onSuccessTitle={t('customerUser.setMainSuccess', {
              name: valueSetMain.name,
            })}
            secondaryButtonTitle={t('general.cancel')}
            primaryButtonTitle={t('general.confirm')}
            isOpen={isOpenSetMain}
            onClose={onCloseSetMain}
            onPerformed={() => {
              customerUserEvents.onCustomerUserSetMain.dispatch();
            }}
            primaryRequest={customerUsersApi.setMain}
            args={[valueSetMain.id]}
          >
            {t('customerUser.setUserMain', { name: valueSetMain.name })}
          </VerifyDialogWithRequest>
        )}
      </BodyOverride>
    </FeatureGuard>
  );
}
